import React, { useState, useCallback } from 'react'
import FrequencyTracking from '../components/analytics/FrequencyTracking'
import { TableScrollWrapper } from '@components/layout/TableScrollWrapper'
import { formatAverageRatingDisplay } from '@utils/statDisplay'
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
  Alert,
  Button,
  Divider,
  Tooltip,
} from '@mui/material'
import {
  TrendingUp,
  TrendingDown,
  TrendingFlat,
  Download,
  AccessTime,
  Star,
  LocalFireDepartment,
  AttachMoney as MoneyIcon,
} from '@mui/icons-material'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  Filler,
} from 'chart.js'
import { Line, Bar } from 'react-chartjs-2'
import {
  useGetPatternsOverviewQuery,
  useGetTimelineQuery,
  useGetPreferencesQuery,
  useGetSmokingTimesQuery,
  useGetRatingCorrelationQuery,
  DateRange,
  Period,
} from '../store/api/analyticsApi'
import { useGetCigarStatsQuery } from '../store/api/cigarApi'

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  ChartTooltip,
  Legend,
  Filler
)

const DATE_RANGE_LABELS: Record<DateRange, string> = {
  '7d': '7 Days',
  '30d': '30 Days',
  '90d': '90 Days',
  '1y': '1 Year',
  all: 'All Time',
}

const PERIOD_LABELS: Record<Period, string> = {
  daily: 'Daily',
  weekly: 'Weekly',
  monthly: 'Monthly',
}

function TrendIcon({ trend }: { trend: 'increasing' | 'decreasing' | 'stable' }) {
  if (trend === 'increasing')
    return <TrendingUp sx={{ color: 'success.main', fontSize: 20 }} />
  if (trend === 'decreasing')
    return <TrendingDown sx={{ color: 'error.main', fontSize: 20 }} />
  return <TrendingFlat sx={{ color: 'text.secondary', fontSize: 20 }} />
}

function StatCard({
  title,
  value,
  subtitle,
  icon,
}: {
  title: string
  value: string | number
  subtitle?: string
  icon?: React.ReactNode
}) {
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {title}
          </Typography>
          {icon}
        </Box>
        <Typography variant="h4" fontWeight="bold">
          {value}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  )
}

function exportToCSV(filename: string, rows: string[][]): void {
  const csv = rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n')
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

const Analytics: React.FC = () => {
  const [dateRange, setDateRange] = useState<DateRange>('30d')
  const [period, setPeriod] = useState<Period>('daily')

  const params = { dateRange }

  const { data: overview, isLoading: overviewLoading, error: overviewError } = useGetPatternsOverviewQuery(params)
  const { data: timeline, isLoading: timelineLoading } = useGetTimelineQuery({ ...params, period })
  const { data: preferences, isLoading: prefLoading } = useGetPreferencesQuery(params)
  const { data: times, isLoading: timesLoading } = useGetSmokingTimesQuery(params)
  const { data: ratings, isLoading: ratingsLoading } = useGetRatingCorrelationQuery(params)
  const { data: collectionStats } = useGetCigarStatsQuery()

  const handleExport = useCallback(() => {
    const rows: string[][] = [
      ['Export: CigarTrack Smoking Patterns', '', '', ''],
      ['Date Range', DATE_RANGE_LABELS[dateRange], '', ''],
      ['', '', '', ''],
      ['=== OVERVIEW ===', '', '', ''],
      ['Total Sessions', String(overview?.totalSessions ?? ''), '', ''],
      ['Average Rating', String(overview?.averageRating ?? 'N/A'), '', ''],
      ['Avg Per Week', String(overview?.avgPerWeek ?? ''), '', ''],
      ['Avg Per Month', String(overview?.avgPerMonth ?? ''), '', ''],
      ['Trend', overview?.trend ?? '', '', ''],
      ['', '', '', ''],
    ]

    if (timeline) {
      rows.push(['=== TIMELINE ===', '', '', ''])
      rows.push(['Period', 'Sessions', 'Avg Rating', ''])
      for (const t of timeline.timeline) {
        rows.push([t.period, String(t.count), String(t.avgRating ?? ''), ''])
      }
      rows.push(['', '', '', ''])
    }

    if (preferences) {
      rows.push(['=== TOP BRANDS ===', '', '', ''])
      rows.push(['Brand', 'Sessions', 'Percentage', 'Avg Rating'])
      for (const b of preferences.brands) {
        rows.push([b.name, String(b.count), `${b.percentage}%`, String(b.avgRating ?? 'N/A')])
      }
      rows.push(['', '', '', ''])
      rows.push(['=== TOP VITOLAS ===', '', '', ''])
      rows.push(['Vitola', 'Sessions', 'Percentage', ''])
      for (const v of preferences.vitolas) {
        rows.push([v.name, String(v.count), `${v.percentage}%`, ''])
      }
    }

    if (times) {
      rows.push(['', '', '', ''])
      rows.push(['=== SMOKING TIMES (HOURLY) ===', '', '', ''])
      rows.push(['Hour', 'Sessions', '', ''])
      for (const h of times.hourly) {
        rows.push([`${h.hour}:00`, String(h.count), '', ''])
      }
    }

    exportToCSV(`cigartrack-patterns-${dateRange}.csv`, rows)
  }, [overview, timeline, preferences, times, dateRange])

  if (overviewError) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>Analytics</Typography>
        <Alert severity="error">Failed to load analytics data. Please try again.</Alert>
      </Box>
    )
  }

  const CHART_COLORS = {
    primary: 'rgba(121, 85, 72, 0.8)',
    primaryBorder: 'rgb(121, 85, 72)',
    primaryFill: 'rgba(121, 85, 72, 0.15)',
    secondary: 'rgba(188, 143, 106, 0.8)',
    secondaryBorder: 'rgb(188, 143, 106)',
  }

  // Timeline chart
  const timelineChartData = {
    labels: timeline?.timeline.map((t) => {
      const d = new Date(t.period)
      if (period === 'monthly') return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
      if (period === 'weekly') return `Week of ${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    }) ?? [],
    datasets: [
      {
        label: 'Sessions',
        data: timeline?.timeline.map((t) => t.count) ?? [],
        borderColor: CHART_COLORS.primaryBorder,
        backgroundColor: CHART_COLORS.primaryFill,
        fill: true,
        tension: 0.3,
        pointRadius: 3,
      },
    ],
  }

  // Hourly times chart
  const hourLabels = Array.from({ length: 24 }, (_, i) => {
    if (i === 0) return '12am'
    if (i < 12) return `${i}am`
    if (i === 12) return '12pm'
    return `${i - 12}pm`
  })

  const timesChartData = {
    labels: hourLabels,
    datasets: [
      {
        label: 'Sessions',
        data: times?.hourly.map((h) => h.count) ?? [],
        backgroundColor: CHART_COLORS.primary,
        borderRadius: 4,
      },
    ],
  }

  // Day of week chart
  const dayChartData = {
    labels: times?.daily.map((d) => d.name.slice(0, 3)) ?? [],
    datasets: [
      {
        label: 'Sessions',
        data: times?.daily.map((d) => d.count) ?? [],
        backgroundColor: CHART_COLORS.secondary,
        borderRadius: 4,
      },
    ],
  }

  // Brands chart
  const brandsChartData = {
    labels: preferences?.brands.map((b) => b.name) ?? [],
    datasets: [
      {
        label: 'Sessions',
        data: preferences?.brands.map((b) => b.count) ?? [],
        backgroundColor: CHART_COLORS.primary,
        borderRadius: 4,
      },
    ],
  }

  // Rating distribution chart
  const ratingChartData = {
    labels: ratings?.distribution.map((r) => (r.rating !== null ? String(r.rating) : 'N/A')) ?? [],
    datasets: [
      {
        label: 'Sessions',
        data: ratings?.distribution.map((r) => r.count) ?? [],
        backgroundColor: CHART_COLORS.primary,
        borderRadius: 4,
      },
    ],
  }

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: { mode: 'index' as const, intersect: false },
    },
    scales: {
      y: { beginAtZero: true, ticks: { stepSize: 1 } },
    },
  }

  const horizontalChartOptions = {
    ...chartOptions,
    indexAxis: 'y' as const,
    scales: {
      x: { beginAtZero: true, ticks: { stepSize: 1 } },
    },
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4" component="h1" fontWeight="bold">
          Smoking Patterns
        </Typography>
        <Tooltip title={!overview ? 'No data to export' : 'Export analytics to CSV'}>
          <span>
            <Button
              variant="outlined"
              startIcon={<Download />}
              onClick={handleExport}
              disabled={!overview}
              size="small"
            >
              Export CSV
            </Button>
          </span>
        </Tooltip>
      </Box>

      {/* Date range filter */}
      <Box sx={{ mb: 3 }}>
        <ToggleButtonGroup
          value={dateRange}
          exclusive
          onChange={(_e, v) => v && setDateRange(v)}
          size="small"
        >
          {(Object.keys(DATE_RANGE_LABELS) as DateRange[]).map((range) => (
            <ToggleButton key={range} value={range}>
              {DATE_RANGE_LABELS[range]}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      {/* Overview cards */}
      {overviewLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : overview ? (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={3}>
            <StatCard
              title="Total Sessions"
              value={overview.totalSessions}
              icon={<LocalFireDepartment sx={{ color: 'primary.main' }} />}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <StatCard
              title="Avg Rating"
              value={formatAverageRatingDisplay(overview.averageRating)}
              icon={<Star sx={{ color: 'warning.main' }} />}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <StatCard
              title="Per Week"
              value={overview.avgPerWeek.toFixed(1)}
              subtitle="sessions/week"
              icon={<TrendIcon trend={overview.trend} />}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <StatCard
              title="Avg Duration"
              value={
                overview.averageDurationMinutes !== null
                  ? `${Math.round(overview.averageDurationMinutes)}m`
                  : '—'
              }
              icon={<AccessTime sx={{ color: 'info.main' }} />}
            />
          </Grid>
          {collectionStats && (
            <>
              <Grid item xs={6} sm={3}>
                <StatCard
                  title="Collection Value"
                  value={`$${collectionStats.totalValue.toFixed(0)}`}
                  icon={<MoneyIcon sx={{ color: 'success.main' }} />}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <StatCard
                  title="Cigars in Collection"
                  value={collectionStats.totalCount}
                  subtitle={`${collectionStats.availableCount} available`}
                  icon={<LocalFireDepartment sx={{ color: 'secondary.main' }} />}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <StatCard
                  title="Unique Brands"
                  value={collectionStats.uniqueBrands}
                  icon={<Star sx={{ color: 'primary.main' }} />}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <StatCard
                  title="Avg Price"
                  value={`$${collectionStats.averageValue.toFixed(2)}`}
                  subtitle="per cigar"
                  icon={<MoneyIcon sx={{ color: 'info.main' }} />}
                />
              </Grid>
            </>
          )}
        </Grid>
      ) : null}

      {/* Frequency Tracking (E3-S7) */}
      <Box sx={{ mb: 3 }}>
        <FrequencyTracking dateRange={dateRange} />
      </Box>

      {/* Timeline chart */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 2,
              flexWrap: 'wrap',
              gap: 1,
            }}
          >
            <Typography variant="h6">Sessions Over Time</Typography>
            <ToggleButtonGroup
              value={period}
              exclusive
              onChange={(_e, v) => v && setPeriod(v)}
              size="small"
              sx={{ flexWrap: 'wrap' }}
            >
              {(Object.keys(PERIOD_LABELS) as Period[]).map((p) => (
                <ToggleButton key={p} value={p}>
                  {PERIOD_LABELS[p]}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>
          {timelineLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : timeline && timeline.timeline.length > 0 ? (
            <TableScrollWrapper sx={{ height: 250 }}>
              <Line data={timelineChartData} options={chartOptions} />
            </TableScrollWrapper>
          ) : (
            <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
              No session data for this period.
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Preferences */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Top Brands
              </Typography>
              {prefLoading ? (
                <CircularProgress size={24} />
              ) : preferences && preferences.brands.length > 0 ? (
                <Box sx={{ height: 200 }}>
                  <Bar data={brandsChartData} options={horizontalChartOptions} />
                </Box>
              ) : (
                <Typography color="text.secondary">No data yet.</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Top Vitolas
              </Typography>
              {prefLoading ? (
                <CircularProgress size={24} />
              ) : preferences && preferences.vitolas.length > 0 ? (
                <>
                  {preferences.vitolas.map((v) => (
                    <Box key={v.name} sx={{ mb: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2">{v.name}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {v.count} ({v.percentage}%)
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          height: 6,
                          borderRadius: 1,
                          bgcolor: 'divider',
                          overflow: 'hidden',
                        }}
                      >
                        <Box
                          sx={{
                            height: '100%',
                            width: `${v.percentage}%`,
                            bgcolor: 'primary.main',
                            borderRadius: 1,
                            transition: 'width 0.5s ease',
                          }}
                        />
                      </Box>
                    </Box>
                  ))}
                </>
              ) : (
                <Typography color="text.secondary">No data yet.</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Wrapper Types
              </Typography>
              {prefLoading ? (
                <CircularProgress size={24} />
              ) : preferences && preferences.wrappers.length > 0 ? (
                <>
                  {preferences.wrappers.map((w) => (
                    <Box key={w.name} sx={{ mb: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2">{w.name}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {w.count} ({w.percentage}%)
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          height: 6,
                          borderRadius: 1,
                          bgcolor: 'divider',
                          overflow: 'hidden',
                        }}
                      >
                        <Box
                          sx={{
                            height: '100%',
                            width: `${w.percentage}%`,
                            bgcolor: 'secondary.main',
                            borderRadius: 1,
                            transition: 'width 0.5s ease',
                          }}
                        />
                      </Box>
                    </Box>
                  ))}
                </>
              ) : (
                <Typography color="text.secondary">No data yet.</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Smoking times */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Preferred Smoking Hours
              </Typography>
              {timesLoading ? (
                <CircularProgress size={24} />
              ) : times ? (
                <Box sx={{ height: 220 }}>
                  <Bar data={timesChartData} options={chartOptions} />
                </Box>
              ) : (
                <Typography color="text.secondary">No data yet.</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Day of Week
              </Typography>
              {timesLoading ? (
                <CircularProgress size={24} />
              ) : times ? (
                <Box sx={{ height: 220 }}>
                  <Bar data={dayChartData} options={chartOptions} />
                </Box>
              ) : (
                <Typography color="text.secondary">No data yet.</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Ratings */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={5}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Rating Distribution
              </Typography>
              {ratingsLoading ? (
                <CircularProgress size={24} />
              ) : ratings && ratings.distribution.length > 0 ? (
                <Box sx={{ height: 220 }}>
                  <Bar data={ratingChartData} options={chartOptions} />
                </Box>
              ) : (
                <Typography color="text.secondary">No rated sessions yet.</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={7}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Top Rated Cigars
              </Typography>
              {ratingsLoading ? (
                <CircularProgress size={24} />
              ) : ratings && ratings.topRated.length > 0 ? (
                <>
                  {ratings.topRated.slice(0, 7).map((cigar, idx) => (
                    <Box key={`${cigar.brandName}-${cigar.name}`}>
                      {idx > 0 && <Divider sx={{ my: 0.75 }} />}
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {cigar.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {cigar.brandName} · {cigar.count} session{cigar.count !== 1 ? 's' : ''}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Star sx={{ color: 'warning.main', fontSize: 16 }} />
                          <Typography variant="body2" fontWeight="bold">
                            {cigar.avgRating.toFixed(1)}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </>
              ) : (
                <Typography color="text.secondary">No rated sessions yet.</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  )
}

export default Analytics
